import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Evo Web Feed Node Schema
 * 
 * This node allows sending, updating, and deleting feed items in the Evo platform.
 * 
 * Features:
 * - Send feed items to current user (information or activity types)
 * - Update existing feed items
 * - Delete feed items by correlationId
 * - User/subscription context automatically derived from authentication
 * - Optional links, prompts, and actions
 * - Optional MFE integration
 * - Support for dates (dueBy)
 * 
 * Note: subscriptionId and userId are automatically obtained from the authenticated user context
 */
const schema: NodePropertySchema = {
  nodeType: 'evo-web-feed',
  version: '1.0.0',

  properties: [
    // ==================================================================
    // RECIPIENT NOTICE
    // ==================================================================
    {
      key: 'recipientNotice',
      label: 'Notification Recipient',
      type: 'notice',
      description:
        '📬 Feed notifications are sent to the **currently authenticated user** who triggers the workflow. The user and subscription context are automatically determined from the workflow execution context.',
      typeOptions: {
        noticeType: 'info',
      },
    },

    // ==================================================================
    // OPERATION
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'send',
      options: [
        { value: 'send', name: 'Send Feed Item' },
        { value: 'update', name: 'Update Feed Item' },
        { value: 'delete', name: 'Delete Feed Item' },
      ],
      description: 'The operation to perform on the feed item',
    },
    
    // ==================================================================
    // REQUIRED FIELDS (ALL OPERATIONS)
    // ==================================================================
    {
      key: 'correlationId',
      label: 'Correlation ID',
      type: 'string',
      required: true,
      default: '',
      placeholder: 'unique-id-123 or {{executionId}}',
      description: 'Unique identifier for this feed item (no spaces allowed - use hyphens or underscores)',
    },
    
    // ==================================================================
    // CONTENT (SEND & UPDATE)
    // ==================================================================
    {
      key: 'title',
      label: 'Title',
      type: 'string',
      required: true,
      default: '',
      placeholder: 'Feed item title',
      description: 'The title of the feed item (max 50 characters)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
        },
      },
      validation: [
        {
          type: 'maxLength',
          value: 50,
          message: 'Title must be 50 characters or less',
        },
      ],
    },
    
    {
      key: 'description',
      label: 'Description',
      type: 'string',
      required: true,
      default: '',
      placeholder: 'Feed item description (supports markdown)',
      description: 'The description of the feed item (max 800 characters, supports markdown)',
      typeOptions: {
        rows: 4,
      },
      displayOptions: {
        show: {
          operation: ['send', 'update'],
        },
      },
      validation: [
        {
          type: 'maxLength',
          value: 800,
          message: 'Description must be 800 characters or less',
        },
      ],
    },
    
    // ==================================================================
    // FEED TYPE (SEND & UPDATE)
    // ==================================================================
    {
      key: 'feedType',
      label: 'Feed Type',
      type: 'options',
      required: false,
      default: 'information',
      options: [
        { value: 'information', name: 'Information' },
        { value: 'activity', name: 'Activity' },
      ],
      description: 'Type of feed item: information (FYI) or activity (requires user action)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
        },
      },
    },
    
    // ==================================================================
    // OPTIONAL: DATE (SEND & UPDATE)
    // ==================================================================
    {
      key: 'includeDate',
      label: 'Include Date',
      type: 'boolean',
      default: false,
      description: 'Add a date to the feed item (e.g., due date)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
        },
      },
    },
    
    {
      key: 'dueDate',
      label: 'Due Date',
      type: 'string',
      default: '',
      placeholder: '{{dueDate}} or 2025-01-20T10:00:00Z',
      description: 'Date/time in ISO format (UTC)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
          includeDate: [true],
        },
      },
    },
    
    {
      key: 'dateType',
      label: 'Date Type',
      type: 'options',
      default: 'dueBy',
      options: [
        { value: 'dueBy', name: 'Due By' },
      ],
      description: 'The type of date (currently only dueBy is supported)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
          includeDate: [true],
        },
      },
    },
    
    // ==================================================================
    // OPTIONAL: LINKS (SEND & UPDATE)
    // ==================================================================
    {
      key: 'includeLinks',
      label: 'Include Links',
      type: 'boolean',
      default: false,
      description: 'Add links that open in a new browser tab',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
        },
      },
    },
    
    {
      key: 'links',
      label: 'Links',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true,
      },
      default: {},
      placeholder: 'Add Link',
      description: 'Links to external resources',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
          includeLinks: [true],
        },
      },
      fixedCollectionOptions: [
        {
          name: 'items',
          displayName: 'Link',
          values: [
            {
              key: 'text',
              label: 'Text',
              type: 'string',
              default: '',
              placeholder: 'View Details',
              required: true,
              description: 'Link text (max 50 characters)',
              validation: [
                {
                  type: 'maxLength',
                  value: 50,
                  message: 'Link text must be 50 characters or less',
                },
              ],
            },
            {
              key: 'desktopUrl',
              label: 'Desktop URL',
              type: 'string',
              default: '',
              placeholder: 'https://example.com or /path/to/page',
              required: false,
              description: 'URL for desktop users (required if Widget ID is not provided). Provide either Desktop URL or Widget ID (or both)',
            },
            {
              key: 'includeMobileUrl',
              label: 'Different Mobile URL',
              type: 'boolean',
              default: false,
              description: 'Specify a different URL for mobile users',
            },
            {
              key: 'mobileUrl',
              label: 'Mobile URL',
              type: 'string',
              default: '',
              placeholder: 'https://m.example.com',
              description: 'URL for mobile users (if different from desktop)',
              displayOptions: {
                show: {
                  includeMobileUrl: [true],
                },
              },
            },
            {
              key: 'includeWidgetId',
              label: 'Include Widget ID',
              type: 'boolean',
              default: false,
              description: 'Deep link into a specific Evo widget/app',
            },
            {
              key: 'widgetId',
              label: 'Widget ID',
              type: 'string',
              default: '',
              placeholder: 'evo.e2e-automation-workflow',
              description: 'Widget/app ID for deep linking (required if Desktop URL is not provided). Provide either Desktop URL or Widget ID (or both)',
              displayOptions: {
                show: {
                  includeWidgetId: [true],
                },
              },
            },
          ],
        },
      ],
    },
    
    // ==================================================================
    // OPTIONAL: PROMPTS (SEND & UPDATE)
    // ==================================================================
    {
      key: 'includePrompts',
      label: 'Include Copilot Prompts',
      type: 'boolean',
      default: false,
      description: 'Add prompts that open in Copilot (max 3, only first is displayed)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
        },
      },
    },
    
    {
      key: 'prompts',
      label: 'Copilot Prompts',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true,
      },
      default: {},
      placeholder: 'Add Prompt',
      description: 'Prompts for Copilot (max 3, only first is displayed)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
          includePrompts: [true],
        },
      },
      fixedCollectionOptions: [
        {
          name: 'items',
          displayName: 'Prompt',
          values: [
            {
              key: 'text',
              label: 'Prompt Text',
              type: 'string',
              default: '',
              placeholder: 'Analyze this data...',
              required: true,
              description: 'The prompt to send to Copilot (max 200 characters)',
              validation: [
                {
                  type: 'maxLength',
                  value: 200,
                  message: 'Prompt text must be 200 characters or less',
                },
              ],
            },
            {
              key: 'includeAgentOptions',
              label: 'Specify Agent',
              type: 'boolean',
              default: false,
              description: 'Use a specific Copilot agent',
            },
            {
              key: 'agentId',
              label: 'Agent ID',
              type: 'string',
              default: '',
              placeholder: 'agent-id',
              description: 'The agent ID to use (max 100 characters)',
              displayOptions: {
                show: {
                  includeAgentOptions: [true],
                },
              },
              validation: [
                {
                  type: 'maxLength',
                  value: 100,
                  message: 'Agent ID must be 100 characters or less',
                },
              ],
            },
            {
              key: 'includePluginName',
              label: 'Specify Plugin',
              type: 'boolean',
              default: false,
              description: 'Use a specific Copilot plugin',
            },
            {
              key: 'pluginName',
              label: 'Plugin Name',
              type: 'string',
              default: '',
              placeholder: 'plugin-name',
              description: 'The plugin name to use (max 100 characters)',
              displayOptions: {
                show: {
                  includePluginName: [true],
                },
              },
              validation: [
                {
                  type: 'maxLength',
                  value: 100,
                  message: 'Plugin name must be 100 characters or less',
                },
              ],
            },
          ],
        },
      ],
    },
    
    // ==================================================================
    // OPTIONAL: ACTIONS (SEND & UPDATE)
    // ==================================================================
    {
      key: 'includeActions',
      label: 'Include Actions',
      type: 'boolean',
      default: false,
      description: 'Add inline action buttons (max 3)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
        },
      },
    },
    
    {
      key: 'actions',
      label: 'Actions',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true,
      },
      default: {},
      placeholder: 'Add Action',
      description: 'Inline action buttons (max 3)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
          includeActions: [true],
        },
      },
      fixedCollectionOptions: [
        {
          name: 'items',
          displayName: 'Action',
          values: [
            {
              key: 'actionId',
              label: 'Action ID',
              type: 'string',
              default: '',
              placeholder: 'approve',
              required: true,
              description: 'Unique identifier for this action',
            },
            {
              key: 'backendId',
              label: 'Backend ID',
              type: 'string',
              default: '',
              placeholder: 'evo.workflow',
              required: true,
              description: 'Backend identifier (e.g., evo.workflow)',
            },
            {
              key: 'text',
              label: 'Button Text',
              type: 'string',
              default: '',
              placeholder: 'Approve',
              required: true,
              description: 'Text displayed on the button',
            },
            {
              key: 'uri',
              label: 'Action URI',
              type: 'string',
              default: '',
              placeholder: '/api/actions/approve',
              required: true,
              description: 'URI/endpoint to call when clicked',
            },
            {
              key: 'variant',
              label: 'Button Variant',
              type: 'options',
              default: 'primary',
              options: [
                { value: 'primary', name: 'Primary' },
                { value: 'secondary', name: 'Secondary' },
                { value: 'tertiary', name: 'Tertiary' },
              ],
              description: 'Visual style of the button (primary, secondary, or tertiary)',
            },
            {
              key: 'includeIcon',
              label: 'Include Icon',
              type: 'boolean',
              default: false,
              description: 'Add an icon to the button',
            },
            {
              key: 'endIcon',
              label: 'Icon Name',
              type: 'string',
              default: '',
              placeholder: 'Check',
              description: 'Icon to display (e.g., Check, X, CircleArrowUpFilled)',
              displayOptions: {
                show: {
                  includeIcon: [true],
                },
              },
            },
          ],
        },
      ],
    },
    
    // ==================================================================
    // OPTIONAL: MFE (SEND & UPDATE)
    // ==================================================================
    {
      key: 'includeMfe',
      label: 'Include MFE',
      type: 'boolean',
      default: false,
      description: 'Associate this feed item with a Micro Frontend Element',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
        },
      },
    },
    
    {
      key: 'mfeId',
      label: 'MFE ID',
      type: 'string',
      default: '',
      placeholder: 'mfe-identifier',
      description: 'Micro Frontend Element identifier (max 250 characters)',
      displayOptions: {
        show: {
          operation: ['send', 'update'],
          includeMfe: [true],
        },
      },
      validation: [
        {
          type: 'maxLength',
          value: 250,
          message: 'MFE ID must be 250 characters or less',
        },
      ],
    },
  ],
  
  metadata: {
    author: 'Evo E2E Team',
    documentation: 'https://evo-docs.access-workspace.com/docs/access-evo-feed/quickstart',
  },
};

export default schema;
